import React, {Fragment, useState} from 'react';

import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {GenericAttachedItemsSidebarItem} from 'app/components/general/GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {PopoverForm, PopoverHeading} from 'app/general/overlays';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {useRemoveTeamItem, useTeams} from 'app/teams/api';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {gt} from 'app/utils/i18n';

import type {OverlayProps} from 'app/general/overlays';
import type {
  CommonTeamMembershipSerializedType,
  TeamSerializedType,
} from 'sweet-types';

interface Props<T> {
  overlayProps: OverlayProps;
  attached?: Array<T>;
  itemURL: string;
  item?: {
    url: string;
    team_memberships: Array<T>;
  };
  addItemToTeam: (team: TeamSerializedType, url: string) => Promise<unknown>;
  disableAndShowTooltipText: (team: T) => string | undefined;
  onSuccess: () => Promise<unknown>;
  getTeamFilterParams: () =>
    | {exclude_document_url: string}
    | {exclude_user_id: string};
}

export function AddToTeamPopover<T extends CommonTeamMembershipSerializedType>({
  overlayProps,
  item,
  itemURL,
  attached,
  addItemToTeam,
  disableAndShowTooltipText,
  onSuccess,
  getTeamFilterParams,
}: Props<T>) {
  const [search, setSearch] = useState<string>('');

  const [deleteMembership] = useRemoveTeamItem();

  const {data: available} = useTeams(
    convertLocationParamsToFetchParams({
      search,
      ...getTeamFilterParams(),
    }),
    {refetchOnMountOrArgChange: false, skip: item == null}
  );

  const doAddItemToTeam = (team) => {
    return addItemToTeam(team, itemURL).then(onSuccess);
  };

  const onRemove = (team) =>
    item != null
      ? deleteMembership({
          teamURL: team.team,
          item,
        })
          .unwrap()
          .then(onSuccess)
      : null;
  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => {
        return (
          <Fragment>
            <PopoverBody className="m-0 p-0 ">
              <StyledScrollableContainerDiv className="d-flex flex-column">
                {attached != null && attached.length > 0 ? (
                  <Fragment>
                    <PopoverHeading
                      key="popoverheading"
                      className="popover-header"
                    >
                      {gt('Teams')}
                    </PopoverHeading>
                    <ul
                      className="list-group list-group-flush flex-grow-1 flex-shrink-0 overflow-auto"
                      style={{
                        maxHeight: 'calc(60vh - 230px)',
                      }}
                    >
                      {attached.map((team) => (
                        <GenericAttachedItemsSidebarItem
                          key={team.team}
                          item={team}
                          nameProcessor={(team) => team.name}
                          htmlURLProcessor={(team) => team.team_html_url}
                          attached={true}
                          disableAndShowTooltipText={disableAndShowTooltipText(
                            team
                          )}
                          onRemove={onRemove}
                          actionTooltip={'Remove from this team'}
                          onSelect={() => Promise.resolve(true)}
                        />
                      ))}
                    </ul>
                  </Fragment>
                ) : null}
                <PopoverHeading>{gt('Add to team')}</PopoverHeading>
                <SearchInputField
                  autoFocus
                  value={search}
                  onChange={(q) => setSearch(q)}
                  placeholder={gt('Search')}
                />
                {available == null ? (
                  <p className="text-center py-3 px-3 mb-0">
                    <i className="fa fa-circle-notch fa-spin mr-2" />
                    {gt('Loading teams')}...
                  </p>
                ) : (
                  <div className="overflow-auto">
                    {available.results.length > 0 ? (
                      <Fragment>
                        {available.results.map((team) => (
                          <GenericAttachedItemsSidebarItem
                            key={team.url}
                            item={team}
                            nameProcessor={(team) => team.name}
                            attached={false}
                            disableAndShowTooltipText={undefined}
                            onRemove={() => undefined}
                            actionTooltip={'Add to this team'}
                            onSelect={() => doAddItemToTeam(team)}
                          />
                        ))}
                      </Fragment>
                    ) : (
                      <NoResultsMessage
                        onSidebar={false}
                        onPopover={true}
                        icon="fa-users"
                        head={gt('No teams found')}
                        classNameOverwrite="my-4 text-center"
                      />
                    )}{' '}
                  </div>
                )}
              </StyledScrollableContainerDiv>
            </PopoverBody>
            <StyledPopoverBottomDIV>
              <button onClick={close} className="btn btn-link btn-sm">
                <i className="fa fa-times" /> {gt('Close')}
              </button>
            </StyledPopoverBottomDIV>
          </Fragment>
        );
      }}
    </PopoverForm>
  );
}

const StyledScrollableContainerDiv = styled.div`
  max-height: calc(100vh - 200px);
  width: 274px;
`;
